import type { ProductListItemDto } from '@/services/platform/api/model';
import { cn } from '@/lib/core';
import { SoldOutTag } from '../banners/sold-out-tag';

function ShopProductListView1GroupGrid({
  products,
  className,
  onProductClick,
}: {
  products: ProductListItemDto[];
  className?: string;
  onProductClick: (publicGuid: string | undefined) => void;
}) {
  // Row 2 is column-swapped vs row 1: index 2 → col-2, index 3 → col-1
  const placements = [
    'col-start-1 row-start-1',
    'col-start-2 row-start-1',
    'col-start-2 row-start-2',
    'col-start-1 row-start-2',
  ];
  return (
    <div className={cn('grid w-full grid-cols-2 gap-2', className)}>
      {products.slice(0, 4).map((p, i) => (
        <button
          key={p.publicGuid}
          type="button"
          onClick={() => onProductClick(p.publicGuid)}
          className={cn('bg-jncquoi-neutral-200 relative h-65 overflow-hidden', placements[i])}
        >
          {p.imageUrl && (
            <img
              src={p.imageUrl}
              alt={p.title ?? ''}
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
          )}
          {p.soldOut && (
            <>
              <div className="absolute inset-0 bg-[rgba(204,204,204,0.6)]" />
              <SoldOutTag className="absolute top-2 left-2" />
            </>
          )}
        </button>
      ))}
    </div>
  );
}

const FEATURE_UNIT_SIZE = 5;

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function ShopProductListView1Feature({
  product,
  align,
  onProductClick,
}: {
  product: ProductListItemDto;
  align: 'left' | 'right';
  onProductClick: (publicGuid: string | undefined) => void;
}) {
  return (
    <div className={cn('flex items-center py-6', align === 'right' ? 'justify-end' : 'justify-start')}>
      <button
        type="button"
        onClick={() => onProductClick(product.publicGuid)}
        className="bg-jncquoi-neutral-200 relative h-98.25 w-76.75 overflow-hidden"
      >
        {product.imageUrl && (
          <img
            src={product.imageUrl}
            alt={product.title ?? ''}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        )}
        {product.soldOut && (
          <>
            <div className="absolute inset-0 bg-[rgba(204,204,204,0.6)]" />
            <SoldOutTag className="absolute top-2 left-2" />
          </>
        )}
      </button>
    </div>
  );
}

// Each unit is 1 feature image followed by a 2x2 grid of 4 products; the feature image alternates sides.
function ShopProductListView1FeatureUnit({
  products,
  align,
  onProductClick,
}: {
  products: ProductListItemDto[];
  align: 'left' | 'right';
  onProductClick: (publicGuid: string | undefined) => void;
}) {
  const feature = products[0];
  const group = products.slice(1, 5);

  return (
    <>
      {feature && (
        <ShopProductListView1Feature product={feature} align={align} onProductClick={onProductClick} />
      )}
      {group.length > 0 && (
        <ShopProductListView1GroupGrid products={group} className="pt-1" onProductClick={onProductClick} />
      )}
    </>
  );
}

export function ShopProductListView1Grid({
  products,
  onProductClick,
}: {
  products: ProductListItemDto[];
  onProductClick: (publicGuid: string | undefined) => void;
}) {
  const pair = products.slice(0, 2);
  const units = chunk(products.slice(2), FEATURE_UNIT_SIZE);

  return (
    <div className="flex flex-col gap-2 px-4 pt-8">
      {pair.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {pair.map(p => (
            <button
              key={p.publicGuid}
              type="button"
              onClick={() => onProductClick(p.publicGuid)}
              className="bg-jncquoi-neutral-200 relative h-65 w-full overflow-hidden"
            >
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt={p.title ?? ''}
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
              )}
              {p.soldOut && (
                <>
                  <div className="absolute inset-0 bg-[rgba(204,204,204,0.6)]" />
                  <SoldOutTag className="absolute top-2 left-2" />
                </>
              )}
            </button>
          ))}
        </div>
      )}
      {units.map((unitProducts, i) => (
        <ShopProductListView1FeatureUnit
          key={i}
          products={unitProducts}
          align={i % 2 === 0 ? 'left' : 'right'}
          onProductClick={onProductClick}
        />
      ))}
    </div>
  );
}
